export type WhiteLayerPreviewMode = "original" | "w1" | "overlay";

export type WhiteLayerSourceType =
  | "single-image"
  | "gangsheet"
  | "processed-image";

export type WhiteLayerExportFormat = "png" | "tiff";

export type WhiteLayerSettings = {
  whiteStrength: number;
  edgeTightness: number;
  darkReduction: number;
  fadeSupport: number;
  noiseCleanup: number;
};

export type WhiteLayerWarningLevel = "info" | "warning" | "danger" | "success";

export type WhiteLayerWarning = {
  id: string;
  level: WhiteLayerWarningLevel;
  title: string;
  message: string;
};

export type WhiteLayerStats = {
  estimatedCoverage: number;
  estimatedDensity: number;
  estimatedFeel: "Light" | "Balanced" | "Heavy";
  haloRisk: "Low" | "Moderate" | "High";
};

export type WhiteLayerUploadedImage = {
  fileName: string;
  width?: number;
  height?: number;
  mimeType?: string;
  objectUrl?: string;
};

export type WhiteLayerPreset = {
  id: string;
  label: string;
  description: string;
  settings: WhiteLayerSettings;
};

export type WhiteLayerExportMode = "preview-only" | "png-w1" | "tiff-w1";

export type WhiteLayerExportTarget = "maintop" | "printexp" | "both";

export type WhiteLayerSingleImagePayload = {
  sourceType: "single-image";
  target: WhiteLayerExportTarget;
  imageBase64: string;
  imageName: string;
  mimeType: string;
  width: number;
  height: number;
  settings: WhiteLayerSettings;
};

export type WhiteLayerProcessedImagePayload = {
  sourceType: "processed-image";
  target: WhiteLayerExportTarget;
  width: number;
  height: number;
  originalImageBase64: string;
  w1MaskBase64: string;
  fileName: string;
  mimeType?: string;
  exportFormat: WhiteLayerExportFormat;
};

export type WhiteLayerPlacedItem = {
  id: string;
  imageBase64: string;
  imageName: string;
  mimeType: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  originalWidth: number;
  originalHeight: number;
};

export type WhiteLayerGangsheetPayload = {
  sourceType: "gangsheet";
  target: WhiteLayerExportTarget;
  sheetWidth: number;
  sheetHeight: number;
  dpi: number;
  items: WhiteLayerPlacedItem[];
  settings: WhiteLayerSettings;
};

export type WhiteLayerExportPayload =
  | WhiteLayerSingleImagePayload
  | WhiteLayerProcessedImagePayload
  | WhiteLayerGangsheetPayload;

export type WhiteLayerExportResponse = {
  success: boolean;
  fileName?: string;
  mimeType?: string;
  downloadUrl?: string;
  error?: string;
};

export const DEFAULT_WHITE_LAYER_SETTINGS: WhiteLayerSettings = {
  whiteStrength: 88,
  edgeTightness: 2,
  darkReduction: 55,
  fadeSupport: 35,
  noiseCleanup: 8,
};

export const WHITE_LAYER_PRESETS: WhiteLayerPreset[] = [
  {
    id: "balanced-logo",
    label: "Balanced Logo",
    description: "Good all-around starting point for solid artwork.",
    settings: {
      whiteStrength: 88,
      edgeTightness: 2,
      darkReduction: 55,
      fadeSupport: 35,
      noiseCleanup: 8,
    },
  },
  {
    id: "soft-fades",
    label: "Soft Fades",
    description: "Better support for semi-transparent edges and soft blends.",
    settings: {
      whiteStrength: 84,
      edgeTightness: 1,
      darkReduction: 48,
      fadeSupport: 62,
      noiseCleanup: 6,
    },
  },
  {
    id: "heavy-pop",
    label: "Heavy Pop",
    description: "Stronger white support for brighter output on light and mid colors.",
    settings: {
      whiteStrength: 96,
      edgeTightness: 2,
      darkReduction: 35,
      fadeSupport: 40,
      noiseCleanup: 10,
    },
  },
  {
    id: "dark-artwork",
    label: "Dark Artwork",
    description: "Pulls white back under darker colors to help reduce stiffness.",
    settings: {
      whiteStrength: 82,
      edgeTightness: 3,
      darkReduction: 74,
      fadeSupport: 28,
      noiseCleanup: 10,
    },
  },
];